import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CustomField, IntakeForm, IntakeFormSection, IntakeOption, Organization, User } from '@ee/common/models';
import { AccountType, IntakeFormSections } from '@ee/common/enums';
import { evictionReasonFieldOf } from '@ee/common/forms';
import { PlaintiffOptions } from '@ee/common/utils';
import { IntakeEvictionFormService } from '../services/intake-eviction-form.service';

@Component({
  selector: 'ee-eviction-property-details',
  template: `
    <form class="flex flex-col justify-start items-stretch" [formGroup]="propertyDetailsForm">
      <ee-expansion-panel
        class="mb-2 md:mb-4"
        header="Plaintiff"
        [expanded]="true"
        [collapsible]="false"
        [fullWidth]="true">
        <div class="flex flex-row" ee-expansion-panel-header-actions>
          <div style="font-size: 14px">{{ plaintiffSectionDescription }}</div>
        </div>
        <!--
          EE-367: one plaintiff field everywhere. The options are the client account's (a restricted
          client may only pick one), a PM property's owners, or public intake's typed company name.
        -->
        <ee-plaintiff-name-field
          [control]="$any(propertyDetailsForm.get('company_name'))"
          [savedNames]="plaintiffOptions?.options ?? []"
          [restricted]="!!plaintiffOptions?.restricted"
          [existingValue]="plaintiffExistingValue"
          label="Plaintiff Name"
          [required]="true"
          [hint]="'Required. ' + (plaintiffSectionLocalization?.['company_name_hint'] ?? '')"></ee-plaintiff-name-field>
      </ee-expansion-panel>
      <ee-expansion-panel
        class="mb-2 md:mb-4"
        header="Tenants"
        [expanded]="true"
        [collapsible]="false"
        [fullWidth]="true">
        <div class="flex flex-row" ee-expansion-panel-header-actions>
          <div style="font-size: 14px">{{ tenantSectionDescription }}</div>
        </div>
        <ee-eviction-tenants-section
          [form]="tenantDetailsForm"
          [options]="options"
          [tenantSection]="tenantSection"
          [reasonField]="reasonField"
          [tenantSectionLocalization]="tenantSectionLocalization"
          [forceMultiSelectReasons]="forceMultiSelectReasons"
          [overrideFieldVisibility]="loggedInOrg?.type === accountTypes.ATTORNEY"></ee-eviction-tenants-section>
      </ee-expansion-panel>
      <ee-expansion-panel
        header="Property Address & Details"
        [expanded]="true"
        [collapsible]="false"
        [fullWidth]="true">
        <div class="flex flex-row" ee-expansion-panel-header-actions>
          <div style="font-size: 14px">{{ propertySectionDescription }}</div>
        </div>
        <ee-eviction-property-section
          [form]="propertyDetailsForm"
          [options]="options"
          [loggedInOrg]="loggedInOrg"
          [loggedInUser]="loggedInUser"
          [allowCourtSelection]="allowCourtSelection"
          [overrideFieldVisibility]="loggedInOrg?.type === accountTypes.ATTORNEY"
          [propertySection]="propertySection"
          [propertySectionLocalization]="propertySectionLocalization"
          [selectedClient]="selectedClient"></ee-eviction-property-section>
      </ee-expansion-panel>
    </form>
  `,
  styles: [
    `
      .margin-bottom {
        margin-bottom: 1rem;
      }
      .margin-bottom-sm {
        margin-bottom: 0.5rem;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EvictionPropertyDetailsComponent implements OnInit {
  @Input() tenantDetailsForm: UntypedFormGroup;

  @Input() propertyDetailsForm: UntypedFormGroup;

  @Input() loggedInOrg?: Organization;

  /**
   * Forwarded to the property section. False on public and other client-facing intake so court
   * stays empty. Null leaves the section on the signed-in org type (the edit dialog).
   */
  @Input() allowCourtSelection: boolean | null = null;

  /** Signed-in user, forwarded to the property section only to gate the court-change-request control. */
  @Input() loggedInUser?: User;

  @Input() selectedAttorneyId: string;

  @Input() selectedClient: Organization | undefined;

  @Input() options: Map<string, IntakeOption> | undefined;

  /** Forwarded to the tenant section unchanged; see its own doc (Req 4.7: edit dialog, or a cloned multi-reason case). */
  @Input() forceMultiSelectReasons = false;

  /** EE-367: what the plaintiff field offers, and whether it is restricted to those names. */
  @Input() plaintiffOptions: PlaintiffOptions | null = null;

  /** EE-367: an existing case's stored plaintiff, still offered when it is no longer an option. */
  @Input() plaintiffExistingValue: string | null = null;

  @Input() overrideFieldVisibility = false;

  @Input() intakeForm: IntakeForm | undefined;

  @Input() intakeEvictionFormService: IntakeEvictionFormService | undefined;

  @Input() propertySection: IntakeFormSection | undefined;

  @Input() tenantSection: IntakeFormSection | undefined;

  protected readonly accountTypes = AccountType;

  /** The server-generated Reason_Field the tenant section renders (Req 4.2); a stable reference from `intakeForm`. */
  get reasonField(): CustomField | undefined {
    return evictionReasonFieldOf(this.intakeForm);
  }

  protected readonly intakeFormSections = IntakeFormSections;

  public plaintiffSectionDescription: string = '';

  public tenantSectionDescription: string = '';

  public propertySectionDescription: string = '';

  public plaintiffSectionLocalization: { [key: string]: string | undefined } = {};

  public tenantSectionLocalization: { [key: string]: string | undefined } = {};

  public propertySectionLocalization: { [key: string]: string | undefined } = {};

  ngOnInit(): void {
    this.plaintiffSectionDescription =
      this.intakeEvictionFormService?.getSectionDescription(IntakeFormSections.PLAINTIFF) ?? '';
    this.tenantSectionDescription =
      this.intakeEvictionFormService?.getSectionDescription(IntakeFormSections.TENANT) ?? '';
    this.propertySectionDescription =
      this.intakeEvictionFormService?.getSectionDescription(IntakeFormSections.PROPERTY) ?? '';
    this.plaintiffSectionLocalization =
      this.intakeEvictionFormService?.getSectionLocalization(IntakeFormSections.PLAINTIFF) ?? {};
    this.tenantSectionLocalization =
      this.intakeEvictionFormService?.getSectionLocalization(IntakeFormSections.TENANT) ?? {};
    this.propertySectionLocalization =
      this.intakeEvictionFormService?.getSectionLocalization(IntakeFormSections.PROPERTY) ?? {};
  }
}
